import { FEATURE_FLAG_KEYS, type FeatureFlagKey } from './featureFlags';
import type { Currency, Region } from '../types/region';
import {
  isStripeConnectCountryCodeSupported,
  STRIPE_CONNECT_SUPPORTED_COUNTRY_CODES,
} from './stripeConnectCountries';

export { FEATURE_FLAG_KEYS };

export type MarketFeatureFlagState = Partial<Record<FeatureFlagKey, boolean>>;

export interface MarketConfig {
  region: Region;
  countryCode: string;
  countryName: string;
  currency: Currency;
  symbol: string;
  locale: string;
  enabledByDefault: boolean;
  surchargePolicy: 'allowed' | 'restricted' | 'not_supported';
  defaultTaxTreatment: 'no_tax_configured' | 'gst_ready' | 'sales_tax_review' | 'vat_ready';
  requiredFeatureFlag?: FeatureFlagKey;
  disabledReason?: string;
}

/** EU member states mapped to the synthetic EU market when that market is enabled. */
export const EU_MEMBER_COUNTRY_CODES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
] as const;

const EU_MEMBER_COUNTRY_SET = new Set<string>(EU_MEMBER_COUNTRY_CODES);

export const MARKET_CONFIG: Record<Region, MarketConfig> = {
  US: {
    region: 'US',
    countryCode: 'US',
    countryName: 'United States',
    currency: 'USD',
    symbol: '$',
    locale: 'en-US',
    enabledByDefault: true,
    surchargePolicy: 'restricted',
    defaultTaxTreatment: 'sales_tax_review',
  },
  AU: {
    region: 'AU',
    countryCode: 'AU',
    countryName: 'Australia',
    currency: 'AUD',
    symbol: 'A$',
    locale: 'en-AU',
    enabledByDefault: true,
    surchargePolicy: 'restricted',
    defaultTaxTreatment: 'gst_ready',
  },
  GB: {
    region: 'GB',
    countryCode: 'GB',
    countryName: 'United Kingdom',
    currency: 'GBP',
    symbol: '£',
    locale: 'en-GB',
    enabledByDefault: true,
    surchargePolicy: 'not_supported',
    defaultTaxTreatment: 'vat_ready',
  },
  EU: {
    region: 'EU',
    countryCode: 'EU',
    countryName: 'European Union',
    currency: 'EUR',
    symbol: '€',
    locale: 'en-IE',
    enabledByDefault: false,
    surchargePolicy: 'not_supported',
    defaultTaxTreatment: 'vat_ready',
    disabledReason: 'Disabled future EU market configuration for later rollout.',
  },
} as const;

export function isMarketEnabled(
  market: MarketConfig,
  featureFlags: MarketFeatureFlagState = {},
): boolean {
  if (market.enabledByDefault) {
    return true;
  }

  return market.requiredFeatureFlag ? featureFlags[market.requiredFeatureFlag] === true : false;
}

export function getActiveMarketConfigs(
  featureFlags: MarketFeatureFlagState = {},
): MarketConfig[] {
  return Object.values(MARKET_CONFIG).filter((market) => isMarketEnabled(market, featureFlags));
}

export function getMarketConfig(
  region: Region,
  featureFlags: MarketFeatureFlagState = {},
): MarketConfig | null {
  const market = MARKET_CONFIG[region];
  if (!market || !isMarketEnabled(market, featureFlags)) {
    return null;
  }

  return market;
}

export function getMarketConfigForCountryCode(
  countryCode: string | null | undefined,
  featureFlags: MarketFeatureFlagState = {},
): MarketConfig | null {
  if (!countryCode) {
    return null;
  }

  const normalized = countryCode.trim().toUpperCase();
  const directMatch = Object.values(MARKET_CONFIG).find(
    (market) => market.countryCode === normalized && isMarketEnabled(market, featureFlags),
  );
  if (directMatch) {
    return directMatch;
  }

  if (EU_MEMBER_COUNTRY_SET.has(normalized)) {
    return getMarketConfig('EU', featureFlags);
  }

  return null;
}

/** ISO country codes where academies may create or use Stripe Connect for lesson payments. */
export function getStripeConnectSupportedCountryCodes(
  _featureFlags: MarketFeatureFlagState = {},
): string[] {
  return [...STRIPE_CONNECT_SUPPORTED_COUNTRY_CODES];
}

export function isStripeConnectCountrySupported(
  countryCode: string | null | undefined,
  _featureFlags: MarketFeatureFlagState = {},
): boolean {
  return isStripeConnectCountryCodeSupported(countryCode);
}

/** Prefer academy.country; fall back to active market country for region (not the synthetic EU code). */
export function resolveAcademyStripeConnectCountry(academy: {
  country?: string | null;
  region?: Region | null;
}): string | null {
  if (academy.country?.trim()) {
    return academy.country.trim().toUpperCase();
  }
  if (!academy.region) {
    return null;
  }
  const market = getMarketConfig(academy.region);
  if (market?.countryCode && market.countryCode !== 'EU') {
    return market.countryCode;
  }
  return academy.region;
}

